import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, fmtQty } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "@/components/common";

export default function Drums() {
  const { lookups } = useAuth();
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [status, setStatus] = useState("all");
  const [minBal, setMinBal] = useState("");
  useEffect(() => {
    const params = { q, brand: brand === "all" ? "" : brand, status: status === "all" ? "" : status, product_id: sp.get("product_id") || "" };
    if (minBal) params.min_balance = minBal;
    api.get("/drums", { params }).then((r) => setItems(r.data));
  }, [q, brand, status, minBal, sp]);
  return (
    <div>
      <PageHeader title="Drum Management" subtitle="Every physical drum has a unique internal ID and QR code.">
        <Button variant="outline" onClick={() => download("/drums-export.xlsx", "Roxy_Drum_Stock.xlsx")} data-testid="drums-export-btn"><Download className="h-4 w-4 mr-1" />Excel</Button>
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input placeholder="Search drum ID, manufacturer no, SKU…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm" data-testid="drums-search-input" />
        <Select value={brand} onValueChange={setBrand}><SelectTrigger className="sm:w-44" data-testid="drums-brand-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All brands</SelectItem>{(lookups?.brands || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="sm:w-40" data-testid="drums-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "full", "partial", "empty", "reversed"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent></Select>
        <Input type="number" placeholder="Min balance" value={minBal} onChange={(e) => setMinBal(e.target.value)} className="sm:w-36" data-testid="drums-min-balance-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="drums-grid">
        {items.map((d) => {
          const pct = d.original_length ? Math.max(0, Math.min(100, (d.current_balance / d.original_length) * 100)) : 0;
          return (
            <Link key={d.id} to={`/drums/${d.id}`} className="min-w-0 rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors" data-testid={`drum-card-${d.drum_code}`}>
              <div className="flex items-start justify-between gap-2 min-w-0">
                <div className="min-w-0">
                  <div className="font-mono font-semibold truncate">{d.drum_code}</div>
                  <div className="text-xs text-muted-foreground">Mfr: <span className="font-mono">{d.mfr_drum_no || "—"}</span></div>
                </div>
                <StatusBadge status={d.status} />
              </div>
              <div className="mt-3 text-sm font-medium">{d.brand} · {d.cable_size}</div>
              <div className="text-xs text-muted-foreground truncate">{d.product_name}</div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full ${pct < 20 ? "bg-red-600" : "bg-[#1E3A8A] dark:bg-blue-500"}`} style={{ width: `${pct}%` }} /></div>
              <div className="mt-2 flex justify-between text-xs font-mono">
                <span data-testid={`drum-balance-${d.drum_code}`}>{fmtQty(d.current_balance, d.uom)}</span>
                <span className="text-muted-foreground">of {fmtQty(d.original_length, d.uom)}</span>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">{d.warehouse}{d.rack ? ` · Rack ${d.rack}` : ""}{d.reserved > 0 ? ` · Reserved ${fmtQty(d.reserved, d.uom)}` : ""}</div>
            </Link>
          );
        })}
      </div>
      {!items.length && <Empty text="No drums match." />}
    </div>
  );
}
